import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";
import { calculateGeoPrefix, fetchMetricMetadata, fetchNationalData, fetchStateData, getGeoPrefix } from "../utils/dashboardUtils";
import useFetchAllIndicators from "../hooks/useFetchAllIndicators";

interface LegendMetricModalProps {
    id: number;
    isOpen: boolean;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    onClose: any;
    svg: string;
};

const LegendMetricModal = ({ id, isOpen, onClose, geoDetails, geoTag, svg }: LegendMetricModalProps) => {
    const [showMetadata, setShowMetadata] = useState(isOpen);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [stateData, setStateData] = useState<DisaggregatedMetric[] | null>(null);
    const [nationalData, setNationalData] = useState<DisaggregatedMetric[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const { disaggregatedIndicators: disaggData } = useFetchAllIndicators(isOpen, geoDetails);

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;

    useEffect(() => {
        setShowMetadata(isOpen);
        if (isOpen && id) {
            fetchAllData();
        }
    }, [isOpen, id]); // Fetch metadata when modal opens or ID changes

    // Fetch metadata, state data, and national data if not already fetched
    const fetchData = async (fetchFunction: Function, setState: React.Dispatch<React.SetStateAction<any>>, dataState: any, id: number, disaggregated?: boolean, geoPrefix?: string) => {
        if (dataState) return;  // If data is already fetched, return early

        setLoading(true);
        try {
            const result = await fetchFunction(id, disaggregated, geoPrefix);
            setState(result ? result.data : null);
        } catch (error) {
            setError(error as Error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllData = async () => {
        const fetchPromises: Promise<void>[] = [];
    
        // Fetch metadata if not already fetched
        if (!metadata) {
            fetchPromises.push(fetchData(fetchMetricMetadata, setMetadata, metadata, id));
        }
    
        // Determine geoPrefix
        const geoPrefix = geoTag?.value 
            ? getGeoPrefix(geoTag.value) 
            : (stateData ? null : calculateGeoPrefix(geoDetails));
    
        // Fetch state data if not already fetched
        if (!stateData && geoPrefix) {
            fetchPromises.push(fetchData(fetchStateData, setStateData, stateData, id, false, geoPrefix));
        }
    
        // Fetch national data if not already fetched
        if (!nationalData) {
            fetchPromises.push(fetchData(fetchNationalData, setNationalData, nationalData, id, false));
        }
    
        // Execute all pending fetch operations in parallel
        if (fetchPromises.length > 0) {
            await Promise.all(fetchPromises);
        }
    };

    const handleClose = () => {
        setShowMetadata(false);
        onClose();
    };

    return (
        <MetricMetadataModal
            isOpen={showMetadata}
            onClose={handleClose}
            disaggData={disaggData}
            metadata={metadata}
            stateData={stateData}
            nationalData={nationalData}
            svg={svg}
            loading={loading}
            error={error}
            triggerRef={triggerRef}
            modalId={modalId}
        />
    );
};


export default LegendMetricModal;
